import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Users, Calendar, Star, BarChart3, Tags, ShieldCheck, ArrowRight, Activity } from "lucide-react";
import { fetchAnalytics } from "@admin/services/adminApi";
import { usePermissions } from "@admin/hooks/usePermissions";
import { PERMISSIONS } from "@admin/constants/permissions";
import { ADMIN_BASE } from "@admin/constants/route";
import { PageHeader, StatCard } from "@admin/components/Primitives";
import PwaAdoptionTile from "@admin/components/PwaAdoptionTile";

const QUICK_LINKS = [
  { to: `${ADMIN_BASE}/pricing`, label: "Pricing & Offers", icon: Tags, perm: PERMISSIONS.MANAGE_PRICING },
  { to: `${ADMIN_BASE}/lawyers`, label: "Lawyers", icon: Briefcase, perm: PERMISSIONS.MANAGE_LAWYERS },
  { to: `${ADMIN_BASE}/consultations`, label: "Consultations", icon: Calendar, perm: PERMISSIONS.MANAGE_CONSULTATIONS },
  { to: `${ADMIN_BASE}/reviews`, label: "Reviews", icon: Star, perm: PERMISSIONS.MANAGE_REVIEWS },
  { to: `${ADMIN_BASE}/users`, label: "Users", icon: Users, perm: PERMISSIONS.MANAGE_USERS },
  { to: `${ADMIN_BASE}/analytics`, label: "Analytics", icon: BarChart3, perm: PERMISSIONS.VIEW_ANALYTICS },
  { to: `${ADMIN_BASE}/team`, label: "Admin Team", icon: ShieldCheck, perm: PERMISSIONS.MANAGE_ADMINS },
];

export default function AdminDashboard() {
  const perms = usePermissions();
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!perms.has(PERMISSIONS.VIEW_ANALYTICS)) return;
    fetchAnalytics().then(setStats).catch((e) => setErr(e?.response?.data?.detail || "Could not load analytics"));
  }, [perms]);

  const visibleLinks = QUICK_LINKS.filter((l) => perms.has(l.perm));

  return (
    <>
      <PageHeader
        testid="admin-dashboard-header"
        title={`Welcome back, ${perms.user?.name?.split(" ")[0] || "Admin"}`}
        subtitle={`You have ${perms.permissions.length} permission${perms.permissions.length === 1 ? "" : "s"} active.`}
      />

      {perms.has(PERMISSIONS.VIEW_ANALYTICS) && stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          <StatCard testid="stat-revenue" label="Customer GMV" value={`₹${(stats.customer_gmv || 0).toLocaleString("en-IN")}`} hint="Gross customer payments (incl. GST)" accent="navy" />
          <StatCard testid="stat-consultations" label="Consultations" value={(stats.consultations || 0).toLocaleString("en-IN")} hint={`${stats.paid_consultations} paid`} accent="gold" />
          <StatCard testid="stat-active-lawyers" label="Active Lawyers" value={`${stats.active_lawyers}/${stats.lawyers}`} hint={`${stats.verified_lawyers} verified`} accent="emerald" />
          <StatCard testid="stat-users" label="Users" value={(stats.users || 0).toLocaleString("en-IN")} hint={`${stats.chats} chats started`} accent="rose" />
        </div>
      )}

      {err && perms.has(PERMISSIONS.VIEW_ANALYTICS) && (
        <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 text-rose-800 px-4 py-3 text-sm" data-testid="dashboard-err">{err}</div>
      )}

      <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-3">Quick Access</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {visibleLinks.map(({ to, label, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            data-testid={`quick-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className="group bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#0A2540] hover:shadow-md transition-all flex items-start gap-3"
          >
            <div className="h-10 w-10 rounded-xl bg-[#0A2540] text-[#D4AF37] grid place-items-center shrink-0">
              <Icon size={18} />
            </div>
            <div className="flex-1">
              <p className="font-bold text-[#0A2540]">{label}</p>
              <p className="text-xs text-slate-500 mt-0.5">Open module →</p>
            </div>
            <ArrowRight size={16} className="text-slate-300 group-hover:text-[#0A2540] mt-1" />
          </Link>
        ))}
      </div>

      {visibleLinks.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center" data-testid="dashboard-no-perms">
          <Activity size={28} className="mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">No modules available — your account has no admin permissions yet. Ask a Super Admin to grant access.</p>
        </div>
      )}

      {perms.has(PERMISSIONS.VIEW_ANALYTICS) && stats && (
        <div className="mt-10 grid lg:grid-cols-2 gap-3">
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-400">Conversion (chat → paid)</p>
            <p className="mt-2 text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
              {stats.conversion_rate == null ? "N/A" : `${stats.conversion_rate}%`}
            </p>
            <p className="mt-1 text-xs text-slate-500">{stats.paid_user_count} of {stats.chat_user_count} AI-chat users went on to pay.</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-400">Pipeline</p>
            <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Active</span><span className="font-bold text-[#0A2540]">{stats.active_consultations}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Pending</span><span className="font-bold text-[#0A2540]">{stats.pending_consultations}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Completed</span><span className="font-bold text-emerald-700">{stats.completed_consultations}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Refunded</span><span className="font-bold text-rose-700">{stats.refunded_consultations}</span></div>
            </div>
          </div>
        </div>
      )}

      {perms.has(PERMISSIONS.VIEW_ANALYTICS) && (
        <div className="mt-3 grid lg:grid-cols-2 gap-3">
          <PwaAdoptionTile days={30} />
        </div>
      )}
    </>
  );
}
